/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react'

import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Unstable_Grid2'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Chip from '@mui/material/Chip'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import IconButton from '@mui/material/IconButton'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Skeleton from '@mui/material/Skeleton'
import Alert from '@mui/material/Alert'
import Divider from '@mui/material/Divider'

import Iconify from 'src/components/iconify'
import Scrollbar from 'src/components/scrollbar'
import {
  AuditEventDetail,
  AuditOverviewRow,
  fetchAuditEventDetail,
  updateContractedTerminals,
} from 'src/service/audit.service'

import { formatCurrency } from '../orders/format-currency'

const fmtDateTime = (value?: string | null) => {
  if (!value) return '—'
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value))
}

type Props = {
  eventRow: AuditOverviewRow | null
  onClose: () => void
  onUpdated: () => void
}

export default function AuditEventDialog({ eventRow, onClose, onUpdated }: Props) {
  const open = !!eventRow
  const [detail, setDetail] = useState<AuditEventDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [contracted, setContracted] = useState('0')
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!eventRow) {
      setDetail(null)
      return
    }
    const load = async () => {
      try {
        setLoading(true)
        const data = await fetchAuditEventDetail(eventRow.eventId)
        setDetail(data)
        setContracted(String(data.event.contractedTerminals || 0))
        setNotes(data.event.billingNotes || '')
      } catch {
        setDetail(null)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [eventRow?.eventId])

  const handleSave = async () => {
    if (!eventRow) return
    try {
      setSaving(true)
      await updateContractedTerminals(eventRow.eventId, Number(contracted) || 0, notes)
      onUpdated()
    } finally {
      setSaving(false)
    }
  }

  const summary = detail?.summary

  return (
    <Dialog open={open} onClose={onClose} maxWidth='lg' fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Stack direction='row' alignItems='center' spacing={1.25}>
          <Iconify icon='solar:shield-check-bold' width={22} sx={{ color: 'primary.main' }} />
          <Box>
            <Typography variant='h6' fontWeight={700}>
              {eventRow?.name}
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              Auditoria detalhada do evento
            </Typography>
          </Box>
        </Stack>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 12, top: 12 }}>
          <Iconify icon='solar:close-circle-bold' width={22} />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Stack spacing={2}>
            <Skeleton variant='rounded' height={80} />
            <Skeleton variant='rounded' height={200} />
            <Skeleton variant='rounded' height={200} />
          </Stack>
        )}
        {!loading && !detail && (
          <Alert severity='error'>Não foi possível carregar os dados do evento.</Alert>
        )}
        {!loading && detail && (
          <Stack spacing={3}>
            {(summary?.overLimit || summary?.undeclared) && (
              <Alert severity={summary?.overLimit ? 'error' : 'warning'}>
                {summary?.overLimit
                  ? `Atenção: ${summary.terminalsUsed} máquinas em uso, mas apenas ${detail.event.contractedTerminals} foram contratadas.`
                  : 'Há máquinas em uso, mas nenhuma foi declarada/contratada para este evento.'}
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid xs={6} md={3}>
                <MiniStat label='Faturamento' value={formatCurrency(summary?.totalRevenue || 0)} />
              </Grid>
              <Grid xs={6} md={3}>
                <MiniStat label='Pedidos' value={String(summary?.totalOrders || 0)} />
              </Grid>
              <Grid xs={6} md={3}>
                <MiniStat
                  label='Máquinas em uso'
                  value={String(summary?.terminalsUsed || 0)}
                  highlight={summary?.overLimit}
                />
              </Grid>
              <Grid xs={6} md={3}>
                <MiniStat label='Vendedores' value={String(summary?.sellersCount || 0)} />
              </Grid>
              <Grid xs={12} md={6}>
                <MiniStat label='Primeira venda' value={fmtDateTime(summary?.firstSale)} />
              </Grid>
              <Grid xs={12} md={6}>
                <MiniStat label='Última venda' value={fmtDateTime(summary?.lastSale)} />
              </Grid>
            </Grid>

            <Paper variant='outlined' sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant='subtitle2' fontWeight={700} gutterBottom>
                Controle de cobrança
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems='flex-start'>
                <TextField
                  label='Máquinas contratadas'
                  type='number'
                  size='small'
                  value={contracted}
                  onChange={(e) => setContracted(e.target.value)}
                  sx={{ width: { xs: 1, sm: 200 } }}
                  inputProps={{ min: 0 }}
                />
                <TextField
                  label='Observações de cobrança'
                  size='small'
                  fullWidth
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
                <Button
                  variant='contained'
                  onClick={handleSave}
                  disabled={saving}
                  startIcon={<Iconify icon='solar:diskette-bold' width={18} />}
                  sx={{ whiteSpace: 'nowrap', height: 40 }}
                >
                  {saving ? 'Salvando...' : 'Salvar'}
                </Button>
              </Stack>
            </Paper>

            <Box>
              <Typography variant='subtitle2' fontWeight={700} gutterBottom>
                Máquinas (terminais) utilizadas
              </Typography>
              <Scrollbar>
                <Table size='small' sx={{ minWidth: 640 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Nº de Série</TableCell>
                      <TableCell align='right'>Pedidos</TableCell>
                      <TableCell align='right'>Faturamento</TableCell>
                      <TableCell align='right'>Vendedores</TableCell>
                      <TableCell align='right'>1º uso</TableCell>
                      <TableCell align='right'>Último uso</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detail.terminals.map((term, idx) => (
                      <TableRow key={`${term.serial}-${idx}`}>
                        <TableCell>
                          <Stack direction='row' alignItems='center' spacing={0.75}>
                            <Iconify
                              icon='solar:smartphone-bold'
                              width={16}
                              sx={{ color: term.hasSerial ? 'primary.main' : 'text.disabled' }}
                            />
                            <Typography variant='body2' fontWeight={600}>
                              {term.serial}
                            </Typography>
                            {!term.hasSerial && (
                              <Chip size='small' color='warning' variant='soft' label='?' />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell align='right'>{term.orderCount}</TableCell>
                        <TableCell align='right'>{formatCurrency(term.revenue)}</TableCell>
                        <TableCell align='right'>{term.sellersCount}</TableCell>
                        <TableCell align='right' sx={{ color: 'text.secondary' }}>
                          {fmtDateTime(term.firstUse)}
                        </TableCell>
                        <TableCell align='right' sx={{ color: 'text.secondary' }}>
                          {fmtDateTime(term.lastUse)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Scrollbar>
            </Box>

            <Divider />

            <Box>
              <Typography variant='subtitle2' fontWeight={700} gutterBottom>
                Vendedores
              </Typography>
              <Scrollbar>
                <Table size='small' sx={{ minWidth: 640 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Vendedor</TableCell>
                      <TableCell align='right'>Pedidos</TableCell>
                      <TableCell align='right'>Faturamento</TableCell>
                      <TableCell align='right'>Máquinas</TableCell>
                      <TableCell align='right'>1ª venda</TableCell>
                      <TableCell align='right'>Última venda</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detail.sellers.map((seller) => (
                      <TableRow key={seller.sellerId}>
                        <TableCell>
                          <Typography variant='body2' fontWeight={600}>
                            {seller.sellerName}
                          </Typography>
                          <Typography variant='caption' color='text.secondary'>
                            {seller.sellerEmail}
                          </Typography>
                        </TableCell>
                        <TableCell align='right'>{seller.orderCount}</TableCell>
                        <TableCell align='right'>{formatCurrency(seller.revenue)}</TableCell>
                        <TableCell align='right'>{seller.terminalsUsed}</TableCell>
                        <TableCell align='right' sx={{ color: 'text.secondary' }}>
                          {fmtDateTime(seller.firstSale)}
                        </TableCell>
                        <TableCell align='right' sx={{ color: 'text.secondary' }}>
                          {fmtDateTime(seller.lastSale)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Scrollbar>
            </Box>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  )
}

function MiniStat({
  label,
  value,
  highlight,
}: {
  label: string
  value: string
  highlight?: boolean
}) {
  return (
    <Paper
      variant='outlined'
      sx={{
        p: 1.5,
        borderRadius: 2,
        borderColor: highlight ? 'error.main' : 'divider',
        bgcolor: highlight ? 'error.lighter' : 'background.paper',
      }}
    >
      <Typography variant='caption' color='text.secondary'>
        {label}
      </Typography>
      <Typography
        variant='subtitle1'
        fontWeight={700}
        sx={{ color: highlight ? 'error.main' : 'text.primary' }}
      >
        {value}
      </Typography>
    </Paper>
  )
}
